// Mirrors Domain\Helios\Support\Scrubber and PathScrubber; Helios re-applies both and is authoritative.

const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g
const URL_PATTERN = /\b(?:[a-z][a-z0-9+.-]*:\/\/|www\.)[^\s<>"']+/gi
const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi
const POSTAL = /\b[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d\b/gi
const TOKEN = /[A-Za-z0-9_-]{24,}/g
const NUMBER = /[+(]?\d(?:[ ()./-]{0,3}\d){5,}/g
const FORMAT_CHARS = /\p{Cf}+/gu
const SPACES = /[\p{Cc}\p{Z}\s]+/gu
const SEGMENT_UNSAFE = /[\p{Cc}\p{Cf}\p{Z}]/gu

const IS_UUID = /^[\da-fA-F]{8}-[\da-fA-F]{4}-[\da-fA-F]{4}-[\da-fA-F]{4}-[\da-fA-F]{12}$/
const QUERY_KEY = /^[A-Za-z0-9_.[\]-]{1,64}$/
const QUERY_VALUE = /^[A-Za-z0-9_.:-]{1,32}$/
const KEY = /^[A-Za-z0-9_.*[\]-]{1,64}$/
const PATH_MAX = 512
const MAX_SEGMENTS = 64

const SAFE_QUERY_KEYS = new Set([
  'page',
  'per_page',
  'sort',
  'direction',
  'date',
  'start_date',
  'end_date',
  'status',
  'tab',
  'view',
  'remember',
  'type',
])

const TOKEN_AFTER = [
  /^links$/,
  /^onsite-book$/,
  /^sign$/,
  /^u$/,
  /^organizations\/invitations$/,
  /^password\/reset$/,
  /^password\/setup$/,
  /^magic-links\/subscription-payment$/,
  /^.*\/calendar$/,
]

function clean(value) {
  if (typeof value !== 'string') {
    return null
  }

  const text = value.replace(FORMAT_CHARS, '').replace(SPACES, ' ').trim()

  return text === '' ? null : text
}

function truncate(value, max) {
  if (value === '') {
    return null
  }

  const chars = Array.from(value)

  if (chars.length <= max) {
    return value
  }

  return `${chars
    .slice(0, Math.max(max - 1, 0))
    .join('')
    .trimEnd()}…`
}

function decode(value, plusIsSpace = false) {
  try {
    return decodeURIComponent(plusIsSpace ? value.replace(/\+/g, ' ') : value)
  } catch {
    return value
  }
}

export function maskText(value, max) {
  const text = clean(value)

  if (text === null) {
    return null
  }

  return truncate(
    text
      .replace(EMAIL, '[email]')
      .replace(URL_PATTERN, '[url]')
      .replace(UUID, '[uuid]')
      .replace(POSTAL, '[postal]')
      .replace(TOKEN, '[token]')
      .replace(NUMBER, '[number]'),
    max
  )
}

export function cleanStr(value, max) {
  const text = clean(value)

  return text === null ? null : truncate(text.replace(EMAIL, '[email]'), max)
}

function scrubSegment(segment, prefix) {
  const decoded = decode(segment)
  const length = Array.from(decoded).length

  if (/^\d+$/.test(segment) || IS_UUID.test(decoded)) {
    return segment
  }

  if (decoded.includes('@')) {
    return '{email}'
  }

  if (
    (prefix !== '' && TOKEN_AFTER.some((pattern) => pattern.test(prefix))) ||
    length > 24 ||
    (length >= 16 && /[A-Za-z]/.test(decoded) && /\d/.test(decoded))
  ) {
    return '{token}'
  }

  // Control, format and space characters are percent-encoded so a path can never break a line or a markdown span.
  return Array.from(segment)
    .slice(0, 64)
    .join('')
    .replace(SEGMENT_UNSAFE, (char) => encodeURIComponent(char))
}

function scrubQuery(query) {
  const seen = new Set()
  const pairs = []

  for (const pair of query.split('&')) {
    if (pair === '') {
      continue
    }

    const index = pair.indexOf('=')
    const key = decode(index === -1 ? pair : pair.slice(0, index), true)
    const value = index === -1 ? null : decode(pair.slice(index + 1), true)

    if (!QUERY_KEY.test(key) || seen.has(key)) {
      continue
    }

    seen.add(key)
    pairs.push(value !== null && SAFE_QUERY_KEYS.has(key) && QUERY_VALUE.test(value) ? `${key}=${value}` : key)
  }

  return pairs.join('&')
}

export function scrubPath(value) {
  if (typeof value !== 'string' || value.trim() === '') {
    return null
  }

  const url = value
    .trim()
    .split('#')[0]
    .replace(/^(?:[a-z][a-z0-9+.-]*:)?\/\/[^/?#]*/i, '')
  const queryIndex = url.indexOf('?')
  const path = queryIndex === -1 ? url : url.slice(0, queryIndex)
  const query = queryIndex === -1 ? '' : url.slice(queryIndex + 1)

  let prefix = ''
  const segments = path
    .split('/')
    .filter((segment) => segment !== '')
    .slice(0, MAX_SEGMENTS)
    .map((segment) => {
      const scrubbed = scrubSegment(segment, prefix)
      prefix = prefix === '' ? segment.toLowerCase() : `${prefix}/${segment.toLowerCase()}`

      return scrubbed
    })

  let scrubbed = `/${segments.join('/')}`
  const keys = query === '' ? '' : scrubQuery(query)

  if (keys !== '') {
    scrubbed += `?${keys}`
  }

  return Array.from(scrubbed).slice(0, PATH_MAX).join('')
}

export function clampInt(value, min, max) {
  const number = typeof value === 'number' ? value : Number.NaN

  return Number.isFinite(number) ? Math.min(Math.max(Math.round(number), min), max) : undefined
}

export function normalizeKey(value) {
  if (typeof value !== 'string') {
    return null
  }

  const key = value
    .split('.')
    .map((segment) => (/^\d+$/.test(segment) ? '*' : segment))
    .join('.')

  return KEY.test(key) ? key : null
}

export function normalizeKeys(values, max) {
  const keys = []

  for (const value of values ?? []) {
    const key = normalizeKey(value)

    if (key !== null && !keys.includes(key)) {
      keys.push(key)
    }

    if (keys.length >= max) {
      break
    }
  }

  return keys
}

export function toKey(value) {
  const text = clean(value)

  if (text === null) {
    return null
  }

  const key = text
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_.*[\]-]/g, '')
    .slice(0, 64)

  return normalizeKey(key)
}
